import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Column, Table as VirtualizedTable } from 'react-virtualized'
import { useContentRect, useHandler } from 'src/hooks'
import { useIntl } from 'react-intl'
import isFunction from 'lodash/isFunction'
import { Checkbox } from 'src/components/inputs'
import { SpinningMask } from 'src/components/masks'
import { LocalizedText } from 'src/components/intl'
import EmptyTablePlaceholder from '../EmptyTablePlaceholder'
import TableHeader from './TableHeader'
import TableRow from './TableRow'
import styles from './Table.scss'

/**
 * A react-virtualized table component.
 */
const Table = ({
  className,
  style,
  headerHeight,
  rowHeight,
  data,
  columns,
  columnData,
  navLinkTo,
  isRowHighlighted,
  onRowClick,
  dndSource,
  loading,
  disabled,
  sort,
  sortBy,
  sortDirection,
  getRowClassName,
  ...props
}) => {
  const ref = React.useRef()
  const { width = 0, height = 0 } = useContentRect(ref)
  const intl = useIntl()

  // provide intl object to all columns cellDataGetter handler
  const localizedColumns = React.useMemo(
    () =>
      columns.map(({ cellDataGetter, ...column }) => ({
        ...column,
        cellDataGetter: cellDataGetter && (params => cellDataGetter({ intl, ...params })),
      })),
    [columns, intl]
  )

  // enhance sort handler in order to provide the cellDataGetter
  const enhancedSort = useHandler(params => {
    if (sort) {
      const { cellDataGetter } = localizedColumns.find(({ dataKey }) => dataKey === params.sortBy)
      const sortWith = cellDataGetter && (rowData => cellDataGetter({ rowData }))
      return sort({ ...params, sortWith })
    }
  })

  return (
    <SpinningMask loading={loading}>
      <div className={className} style={{ ...style, position: 'relative' }}>
        <div ref={ref} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <VirtualizedTable
            width={props.hasOwnProperty('width') ? props.width : width}
            height={props.hasOwnProperty('height') ? props.height : height}
            headerHeight={headerHeight}
            rowHeight={rowHeight}
            rowCount={data.length}
            rowGetter={({ index }) => data[index]}
            gridStyle={{ outline: 'none' }}
            onRowClick={disabled ? undefined : onRowClick}
            rowRenderer={rowProps => {
              const to = navLinkTo && navLinkTo(rowProps)
              return (
                <TableRow
                  dndSource={dndSource}
                  to={to}
                  tabIndex={onRowClick || to ? 0 : undefined}
                  {...rowProps}
                  className={classnames(
                    styles.row,
                    {
                      [styles.clickable]: onRowClick || to,
                      [styles.highlighted]: isRowHighlighted && isRowHighlighted(rowProps),
                      [styles.striped]: !(rowProps.index % 2),
                    },
                    isFunction(getRowClassName) ? getRowClassName(rowProps) : '',
                    rowProps.className
                  )}
                />
              )
            }}
            scrollToAlignment="start"
            {...props}
            headerClassName={styles.header}
            sort={enhancedSort}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {localizedColumns.map(
              ({
                dataKey,
                align = 'left',
                overflow,
                checkable,
                cellRenderer,
                cellDataGetter,
                action,
                ...columnProps
              }) => (
                <Column
                  key={dataKey}
                  dataKey={dataKey}
                  columnData={{ ...columnData, ...columnProps.columnData }}
                  headerRenderer={props => (
                    <TableHeader disabled={disabled} data={data} checkable={checkable} action={action} {...props} />
                  )}
                  {...columnProps}
                  cellRenderer={
                    checkable
                      ? cellProps => {
                          const { getValue, getItem, getSetter } = checkable
                          return (
                            <Checkbox
                              checked={getValue(cellProps).includes(getItem(cellProps))}
                              disabled={disabled}
                              onChange={checked =>
                                getSetter(cellProps)(
                                  !checked
                                    ? getValue(cellProps).filter(item => item !== getItem(cellProps))
                                    : [...getValue(cellProps), getItem(cellProps)]
                                )
                              }
                              label={
                                cellRenderer
                                  ? cellRenderer(cellProps)
                                  : cellDataGetter
                                  ? cellDataGetter(cellProps)
                                  : null
                              }
                            />
                          )
                        }
                      : cellRenderer
                  }
                  cellDataGetter={cellDataGetter}
                  className={classnames(columnProps.className, styles[align], {
                    [styles.overflow]: overflow,
                  })}
                  headerClassName={styles[align]}
                />
              )
            )}
          </VirtualizedTable>
        </div>
      </div>
    </SpinningMask>
  )
}

const noRowsRenderer = () => (
  <EmptyTablePlaceholder>
    <LocalizedText id="WEB_NO_DATA" />
  </EmptyTablePlaceholder>
)

Table.defaultProps = {
  headerHeight: 50,
  rowHeight: 50,
  data: [],
  columns: [],
  noRowsRenderer,
}

Table.propTypes = {
  /**
   * A classname to apply to the table container.
   */
  className: PropTypes.string,
  /**
   * A style object to apply to the table container.
   */
  style: PropTypes.object,
  /**
   * The table width in pixels.
   * If not specified, the table will use the width & height from the resize observer.
   */
  width: PropTypes.number,
  /**
   * The table height in pixels.
   * If not specified, the table will use the width & height from the resize observer.
   */
  height: PropTypes.number,
  /**
   * The table header height in pixels.
   */
  headerHeight: PropTypes.number,
  /**
   * The table rows height in pixels.
   */
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /**
   * The table columns description.
   * Accepts all the props of the react-virtualized Column component and some custom props.
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * A custom cell data getter (with intl prop automatically injected to args).
       */
      cellDataGetter: PropTypes.func,
      /**
       * A custom cell render prop (with intl prop automatically injected to args).
       */
      cellRenderer: PropTypes.func,
      /**
       * The column alignement.
       */
      align: PropTypes.oneOf(['left', 'center', 'right']),
      /**
       * Whether the column content should be shown if it overflows.
       */
      overflow: PropTypes.bool,
    })
  ),
  /**
   * Optional column data to forward to every cellDataGetter and cellRenderer.
   */
  columnData: PropTypes.object,
  /**
   * The table data.
   */
  data: PropTypes.arrayOf(PropTypes.any),
  /**
   * The drag n' drop source id.
   */
  dndSource: PropTypes.elementType,
  /**
   * Whether the table is busy loading some content.
   */
  loading: PropTypes.bool,
  /**
   * Whether the table interraction is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * A function that, if specified, will be called for each row with the current row data as argument.
   * Its result will be used as the `to` prop of a NavLink component applied to the row.
   */
  navLinkTo: PropTypes.func,
  /**
   * A function that, if specified, will be called for each row with the current row data as argument.
   * Its result will be used to determine if the row should be highlighted or not.
   */
  isRowHighlighted: PropTypes.func,
  /**
   * A function that, if specified, will be called for each row with the current row data as argument.
   * Its result will be used to determine the classname of the row.
   */
  getRowClassName: PropTypes.func,
  /**
   * A function that, if specified, will be called on a row click with the current row data as argument.
   */
  onRowClick: PropTypes.func,
  /**
   * A function that, if specified, will be called on a column header click.
   */
  sort: PropTypes.func,
  /**
   * dataKey of the column currently sorted
   */
  sortBy: PropTypes.string,
  /**
   * direction of the current sort
   */
  sortDirection: PropTypes.string,
}

export default React.memo(Table)
